import { ReactNode, useEffect, useRef, useState } from "react"
import { XIcon } from "lucide-react"
import clsx from "classnames"
import { useSubscribedNewslettersContext } from "modules/newsletters/contexts"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { useNewslettersV2Context } from "../../../contexts"

interface StickySubscriptionBarProps {
  children: ReactNode
}

/**
 * Sticky container that manages sticky behavior, close logic, and count badge.
 * Shows a count badge when not subscribed and a close button when subscribed.
 */
export const StickySubscriptionBar = ({ children }: StickySubscriptionBarProps) => {
  const [isClosed, setIsClosed] = useState(false)
  const [isStuck, setIsStuck] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const { ids: newsletterListIds } = useSubscribedNewslettersContext()
  const { isSubscribed } = useNewslettersV2Context()
  const t = useTranslations("newsletters")

  const selectedCount = newsletterListIds.size

  // Simple intersection observer to detect when sticky is active
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is fully visible (ratio = 1), sticky is NOT stuck
        // When sentinel scrolls out of view (ratio < 1), sticky IS stuck
        setIsStuck(entry.intersectionRatio < 1)
      },
      { threshold: [1] },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      {/* Sentinel element to detect when sticky triggers */}
      <div ref={sentinelRef} className="pointer-events-none -mt-px h-px" />

      <div
        className={clsx("z-10 mr-[calc(-50vw+50%)] ml-[calc(-50vw+50%)] w-screen bg-white py-4", {
          "sticky top-0": !isClosed,
          "border-b border-gray-200 shadow-lg": isStuck && !isClosed,
        })}
      >
        <div className="flex flex-col items-center justify-center px-4 sm:px-6">
          {children}

          {isSubscribed && !isClosed && isStuck && (
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsClosed(true)}
                className="flex size-10 items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100"
              >
                <XIcon className="size-5" />
              </button>
            </div>
          )}
        </div>

        {!isSubscribed && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
            <div
              className="inline-flex translate-y-1/2 rounded-full bg-indigo-500 px-3 py-1 text-center text-sm whitespace-nowrap text-white"
              aria-live="polite"
            >
              {selectedCount === 0
                ? t("v2.sticky_bar.select_at_least_one")
                : t(`v2.sticky_bar.selected_count.${selectedCount === 1 ? "one" : "other"}`, {
                    count: selectedCount,
                  })}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
